import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Backdrop,
    Box,
    Chip,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { baseUrl } from '../../environment/environment';

interface PublicStockItemDto {
    articleName: string;
    manufacturer: string;
    innerCode: string;
    quantity: number;
    dimensionType: string;
    siteName: string;
    tags: string[];
}

function getQueryParam(name: string): string | null {
    const params = new URLSearchParams(window.location.search);
    return params.get(name);
}

function parseCsvParam(name: string): string[] {
    const raw = getQueryParam(name);
    if (!raw) return [];
    return raw
        .split(',')
        .map((t) => t.trim())
        .filter((t) => t.length > 0);
}

export default function PublicStockPage(): React.ReactElement {
    const apiKey = useMemo(() => getQueryParam('apiKey'), []);
    const initialTags = useMemo(() => parseCsvParam('tags'), []);
    const initialSites = useMemo(() => parseCsvParam('sites'), []);

    const [items, setItems] = useState<PublicStockItemDto[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [selectedTags, setSelectedTags] = useState<string[]>(initialTags);
    const [availableTags, setAvailableTags] = useState<string[]>([]);
    const [selectedSites, setSelectedSites] = useState<string[]>(initialSites);
    const [availableSites, setAvailableSites] = useState<string[]>([]);

    useEffect(() => {
        if (!apiKey) return;
        let cancelled = false;
        const controller = new AbortController();
        const fetchOptions = async (path: string, setter: (v: string[]) => void) => {
            try {
                const url = new URL(`${baseUrl}/api/public/stock/${path}`);
                url.searchParams.set('apiKey', apiKey);
                const res = await fetch(url.toString(), { signal: controller.signal });
                if (!res.ok) return;
                const data: string[] = await res.json();
                if (!cancelled) setter(data);
            } catch {
                /* ignore — filter still usable */
            }
        };
        fetchOptions('tags', setAvailableTags);
        fetchOptions('sites', setAvailableSites);
        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [apiKey]);

    useEffect(() => {
        if (!apiKey) {
            setError('Nedostaje apiKey u URL parametrima.');
            setItems([]);
            return;
        }

        let cancelled = false;
        const controller = new AbortController();

        const load = async () => {
            try {
                const url = new URL(`${baseUrl}/api/public/stock`);
                url.searchParams.set('apiKey', apiKey);
                selectedTags.forEach((t) => url.searchParams.append('tag', t));
                selectedSites.forEach((s) => url.searchParams.append('site', s));
                const res = await fetch(url.toString(), { signal: controller.signal });
                if (!res.ok) {
                    throw new Error(`HTTP ${res.status}`);
                }
                const data: PublicStockItemDto[] = await res.json();
                if (!cancelled) {
                    setItems(data);
                    setError(null);
                }
            } catch (err) {
                if (cancelled) return;
                const message = err instanceof Error ? err.message : 'Greška prilikom učitavanja.';
                setError(message);
                setItems([]);
            }
        };

        setItems(null);
        const handle = window.setTimeout(load, 250);
        return () => {
            cancelled = true;
            controller.abort();
            window.clearTimeout(handle);
        };
    }, [apiKey, selectedTags, selectedSites]);

    if (items === null) {
        return (
            <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open>
                <CircularProgress color="inherit" />
            </Backdrop>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, textAlign: 'start' }}>
            <Typography variant="h4" sx={{ mb: 2 }}>
                Stanje na magacinima
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                <Autocomplete
                    multiple
                    size="small"
                    options={availableTags}
                    value={selectedTags}
                    onChange={(_, value) => setSelectedTags(value)}
                    sx={{ minWidth: 320, flex: 1, maxWidth: 560 }}
                    renderInput={(params) => <TextField {...params} label="Filter po tagovima" />}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                            const { key, ...chipProps } = getTagProps({ index });
                            return <Chip key={key} label={option} size="small" {...chipProps} />;
                        })
                    }
                />
                <Autocomplete
                    multiple
                    size="small"
                    options={availableSites}
                    value={selectedSites}
                    onChange={(_, value) => setSelectedSites(value)}
                    sx={{ minWidth: 320, flex: 1, maxWidth: 560 }}
                    renderInput={(params) => <TextField {...params} label="Filter po magacinima" />}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                            const { key, ...chipProps } = getTagProps({ index });
                            return <Chip key={key} label={option} size="small" {...chipProps} />;
                        })
                    }
                />
                <Typography variant="body2" color="text.secondary">
                    {items.length} stavki
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Šifra</TableCell>
                            <TableCell>Proizvođač</TableCell>
                            <TableCell>Naziv</TableCell>
                            <TableCell align="right">Količina</TableCell>
                            <TableCell>Magacin</TableCell>
                            <TableCell>Tagovi</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.map((it, idx) => (
                            <TableRow key={`${it.innerCode}-${it.siteName}-${idx}`}>
                                <TableCell>
                                    <code>{it.innerCode}</code>
                                </TableCell>
                                <TableCell>{it.manufacturer}</TableCell>
                                <TableCell>{it.articleName}</TableCell>
                                <TableCell align="right">
                                    {it.quantity} {it.dimensionType}
                                </TableCell>
                                <TableCell>{it.siteName}</TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {it.tags.map((tag) => (
                                            <Chip key={tag} label={tag} size="small" />
                                        ))}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                        {items.length === 0 && !error && (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    Nema robe za zadate uslove.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
